import { Component, computed, inject, input, output } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatError } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { dsDtos } from '@vectronic/openApi-admin';
import { Error, ParameterService } from '@vectronic/parameter';
import {
  DeviceTypeParameterConfigWithAvailableListAndParameter,
  ParameterFormatService,
  ParameterWithData,
} from '@vectronic/util';
import { ParameterCollarsConfigComponent } from '../parameter-collars-config/parameter-collars-config.component';
import { ParameterDateTimeConfigComponent } from '../parameter-date-time-config/parameter-date-time-config.component';
import { ParameterInputConfigComponent } from '../parameter-input-config/parameter-input-config.component';
import { ParameterPatternInputConfigComponent } from '../parameter-pattern-input-config/parameter-pattern-input-config.component';
import { ParameterSelectionConfigComponent } from '../parameter-selection-config/parameter-selection-config.component';
import { ParameterTimeConfigComponent } from '../parameter-time-config/parameter-time-config.component';
import { ParameterTimespanConfigComponent } from '../parameter-timespan-config/parameter-timespan-config.component';
import { ParameterTimezoneConfigComponent } from '../parameter-timezone-config/parameter-timezone-config.component';
import { ParameterVisibilityFlagsConfigComponent } from '../parameter-visibility-flags-config/parameter-visibility-flags-config.component';

@Component({
  selector: 'vectronic-parameter-value',
  imports: [
    MatIcon,
    MatIconButton,
    ParameterCollarsConfigComponent,
    ParameterInputConfigComponent,
    ParameterSelectionConfigComponent,
    ParameterTimeConfigComponent,
    ParameterTimespanConfigComponent,
    ParameterTimezoneConfigComponent,
    MatError,
    ParameterPatternInputConfigComponent,
    ParameterDateTimeConfigComponent,
    ParameterVisibilityFlagsConfigComponent,
    MatTooltip,
  ],
  templateUrl: './parameter-value.component.html',
  styleUrl: './parameter-value.component.scss',
})
export class ParameterValueComponent {
  private parameterFormatService = inject(ParameterFormatService);

  presetPatternParameter = input<dsDtos.Parameter>();
  isEditable = input(false);
  parameterWithData = input<ParameterWithData>();
  deviceTypeParameterConfigWithAvailableList =
    input<dsDtos.DeviceTypeParameterConfigWithAvailableList[]>();
  collarIds = input<number[]>();
  hideLabel = input<boolean>();
  showInfo = input(true);
  showFactory = input(true);
  showEditButton = input<boolean>();
  isEdit = input(false);
  hasUndo = input(false);
  showReset = input(false);
  disabled = input(false);
  showOnlyFactoryLabel = input(false);
  subsystems = input<dsDtos.Subsystem[]>();

  changeParameterData = output<dsDtos.ParameterData>();
  parameterUndo = output<DeviceTypeParameterConfigWithAvailableListAndParameter>();
  changeEdit = output<boolean>();
  resetToFactory = output<DeviceTypeParameterConfigWithAvailableListAndParameter>();

  currentValue = computed(() => {
    const { user, factory } = this.parameterWithData();
    return user?.data || factory?.data || '';
  });
  factoryValue = computed(() => {
    const { factory } = this.parameterWithData();
    return factory?.data || '';
  });

  userValue = computed(() => {
    const { user } = this.parameterWithData();
    return user?.data || '';
  });

  valueType = computed(() => this.parameterWithData().parameter?.value);
  isDisabled = computed(() => !this.parameterWithData()?.parameter?.write || this.disabled());

  config = computed<DeviceTypeParameterConfigWithAvailableListAndParameter>(() => ({
    parameter: this.parameterWithData().parameter,
    ...(this.deviceTypeParameterConfigWithAvailableList()?.find?.(
      ({ parameterId }) => parameterId === this.parameterWithData().parameter.id,
    ) || { parameterId: undefined, userAccess: undefined }),
  }));

  isValid = computed(() => {
    const { user, factory, parameter } = this.parameterWithData();
    const parameterData = user || factory;
    if (!parameterData?.data) return true;
    const deviceContext = {
      device: { pcbTypeId: undefined, firmwareVersionId: undefined, parameterDataList: [] },
      parameterConfigs: [this.config()],
    };
    const parameterContext = {
      parameters: [parameter],
      subsystems: this.subsystems(),
      firmwareVersions: [],
      pcbTypes: [],
    };
    try {
      return ParameterService.isValid(parameterData, deviceContext, parameterContext);
    } catch (e: any) {
      return this.isSupressedValidationError(e);
    }
  });

  private isSupressedValidationError(error: string) {
    return (
      [
        Error.PARAMETER_REQUIRES_PCBTYPE,
        Error.PARAMETER_REQUIRES_FIRMWARE,
        Error.MAX_BEACON_FREQUENCY_PARAMETER_NOT_SET,
        Error.MIN_BEACON_FREQUENCY_PARAMETER_NOT_SET,
      ] as string[]
    ).includes(error);
  }

  errorMessage() {
    const { user, factory, parameter } = this.parameterWithData();
    const parameterData = user || factory;
    const deviceContext = {
      device: { pcbTypeId: undefined, firmwareVersionId: undefined, parameterDataList: [] },
      parameterConfigs: [this.config()],
    };
    const parameterContext = {
      parameters: [parameter],
      subsystems: this.subsystems(),
      firmwareVersions: [],
      pcbTypes: [],
    };
    try {
      ParameterService.isValid(parameterData, deviceContext, parameterContext);
    } catch (e: any) {
      if (!this.isSupressedValidationError(e)) return e;
    }
    return '';
  }

  readOnlyFormat() {
    return this.parameterFormatService.getReadOnlyFormat(
      this.parameterWithData(),
      this.presetPatternParameter(),
      this.showFactory(),
      this.showOnlyFactoryLabel(),
      this.subsystems(),
    );
  }

  displayText = computed(() => {
    const fullText = this.readOnlyFormat();
    if (!fullText) return '';
    return fullText.length > 50 ? fullText.slice(0, 50) + '...' : fullText;
  });

  shouldShowTooltip = computed(() => {
    const fullText = this.readOnlyFormat();
    return !!fullText && fullText.length > 50;
  });

  onClickEdit() {
    this.changeEdit.emit(true);
  }

  onParameterChange($event: {
    config: DeviceTypeParameterConfigWithAvailableListAndParameter;
    value: string;
  }) {
    this.changeParameterData.emit({
      parameterId: $event.config.parameter.id,
      data: $event.value,
      isUserData: this.parameterWithData().parameter.isAvailableAsUserParameter,
    });
  }

  onClose() {
    this.changeEdit.emit(false);
  }

  resetToFactoryTooltip = computed(() => {
    const { factory, parameter } = this.parameterWithData();
    const formattedFactory = this.parameterFormatService.getFormattedData(
      factory,
      parameter,
      this.presetPatternParameter(),
    );
    const base = 'Reset to factory value';
    return formattedFactory ? `${base}: ${formattedFactory}` : base;
  });
}
